import { useNavigate } from "react-router-dom";
import { Heart, Home, Users, ArrowRight, Shield, Lightbulb, ArrowLeft } from "lucide-react";
import "@/styles/govtech.css";

const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

export default function AboutUs() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft size={20} />
            <span>Home</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>

      {/* Hero - Simple and Heartfelt */}
      <section className="bg-[#333333] text-white py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="w-20 h-20 rounded-full bg-[#00703C] bg-opacity-20 flex items-center justify-center mx-auto mb-8">
            <Heart className="text-[#00703C]" size={40} />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            We Believe Everyone Deserves<br />
            <span className="text-[#00703C]">A Place to Call Home</span>
          </h1>
          
          <p className="text-lg text-gray-300">
            TenURes exists for one simple reason: to make the housing system work better for the people it's meant to serve.
          </p>
        </div>
      </section>

      {/* The Why - Personal Story */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-4">Why We Built This</h2>
            <div className="w-16 h-1 bg-[#00703C] mx-auto"></div>
          </div>
          
          <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
            <p>
              After years of working on the frontlines of social housing, we saw the same story play out 
              again and again: good people, facing life's toughest moments, struggling to navigate a system 
              that felt designed to confuse them.
            </p>
            
            <p>
              A widow wondering if she could stay in her home after losing her husband. 
              A family unsure if they qualified to buy the house they'd lived in for twenty years. 
              A pensioner rattling around in a three-bedroom flat, wanting to downsize but not knowing where to start.
            </p>
            
            <p className="font-medium text-[#333333]">
              These aren't just "cases" or "applications." They're people's lives.
            </p>
            
            <p>
              We watched residents wait weeks for answers that should take minutes. We saw housing officers 
              buried in paperwork, unable to give people the time they deserved. We heard the frustration, 
              the confusion, the fear.
            </p>
            
            <p>
              So we asked ourselves: <em>what if we could take everything we've learned—every rule, 
              every calculation, every hard-won insight—and put it directly in residents' hands?</em>
            </p>
            
            <p className="text-xl font-semibold text-[#00703C]">
              That's TenURes.
            </p>
          </div>
        </div>
      </section>

      {/* What We're Fighting For */}
      <section className="py-16 bg-[#F8F8F8]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#333333] text-center mb-12">What We're Fighting For</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="text-[#00703C]" size={28} />
              </div>
              <h3 className="font-bold text-[#333333] mb-2">Clarity</h3>
              <p className="text-gray-600 text-sm">
                No more legal jargon or confusing forms. Just plain English answers to the questions that matter most.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center mx-auto mb-4">
                <Shield className="text-[#00703C]" size={28} />
              </div>
              <h3 className="font-bold text-[#333333] mb-2">Confidence</h3>
              <p className="text-gray-600 text-sm">
                When you walk into that housing office, you'll know exactly where you stand and what evidence you need.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center mx-auto mb-4">
                <Users className="text-[#00703C]" size={28} />
              </div>
              <h3 className="font-bold text-[#333333] mb-2">Dignity</h3>
              <p className="text-gray-600 text-sm">
                Everyone deserves professional guidance, not just those who can afford a solicitor.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Promise */}
      <section className="py-16 bg-[#00703C] text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Our Promise</h2>
          <p className="text-xl text-green-100 leading-relaxed mb-8">
            We'll never charge residents a penny for our core tools. We'll always be transparent about how we use your data.
            And we'll never stop working to make the housing system fairer for everyone.
          </p>
          <p className="text-green-200">
            Because your home isn't just a case number. It's where your life happens.
          </p>
        </div>
      </section>

      {/* Simple CTA */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Home className="mx-auto text-[#00703C] mb-6" size={48} />
          <h2 className="text-2xl font-bold text-[#333333] mb-4">Ready to Get Started?</h2>
          <p className="text-gray-600 mb-8">
            Whether you're a resident looking for answers or a housing team looking for better tools, we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/resident')}
              className="govtech-btn govtech-btn-primary"
            >
              I'm a Resident <ArrowRight size={18} className="ml-2" />
            </button>
            <button
              onClick={() => navigate('/')}
              className="govtech-btn govtech-btn-secondary"
            >
              I'm a Housing Professional
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <TenuresLogo />
          <p className="text-sm text-gray-500 mt-4">
            © {new Date().getFullYear()} Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk — Making Housing Work for Everyone
          </p>
        </div>
      </footer>
    </div>
  );
}
